import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp, Search } from "lucide-react"

import { cn } from "@/lib/utils"
import { normSearch, nodeText } from "@/lib/searchText"

const SearchCtx = React.createContext(null)
const MIN_SEARCH_ITEMS = 7

const Select = SelectPrimitive.Root

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}>
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectContent = React.forwardRef(({ className, children, position = "popper", noSearch, ...props }, ref) => {
  const [q, setQ] = React.useState("")
  const [count, setCount] = React.useState(0)
  const enabled = !noSearch && count >= MIN_SEARCH_ITEMS
  const [empty, setEmpty] = React.useState(false)
  const viewRef = React.useRef(null)
  const inputRef = React.useRef(null)
  const autoDone = React.useRef(false)
  const onFocusCapture = (e) => {
    if (!enabled || autoDone.current || e.target.getAttribute("role") !== "option") return
    autoDone.current = true
    inputRef.current?.focus({ preventScroll: true })
  }
  const register = React.useCallback(() => { setCount((c) => c + 1); return () => setCount((c) => c - 1) }, [])
  React.useEffect(() => {
    if (!enabled) return undefined
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 60)
    return () => clearTimeout(t)
  }, [enabled])
  React.useEffect(() => {
    if (!enabled || !viewRef.current) return
    setEmpty(!!q && viewRef.current.querySelectorAll('[role="option"]:not([data-search-hidden])').length === 0)
  }, [q, enabled, children])
  const ctx = React.useMemo(() => ({ q: enabled ? normSearch(q) : "", active: enabled, register }), [q, enabled, register])
  const onKey = (e) => {
    autoDone.current = true
    if (e.key === "Escape" || e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Tab") return
    if (e.key === "Enter") {
      e.preventDefault()
      viewRef.current?.querySelector('[role="option"]:not([data-search-hidden])')?.focus()
    }
    e.stopPropagation()
  }
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        onFocusCapture={onFocusCapture}
        {...props}>
        {enabled && (
          <div className="sticky top-0 z-10 border-b bg-popover p-1.5" data-testid="select-search-wrap">
            <div className="relative">
              <Search className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKey}
                onPointerDown={(e) => e.stopPropagation()}
                placeholder="ابحث..."
                dir="auto"
                className="h-8 w-full rounded-md border border-input bg-transparent pr-7 pl-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                data-testid="select-search-input" />
            </div>
          </div>
        )}
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          ref={viewRef}
          className={cn("p-1", position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]")}>
          <SearchCtx.Provider value={ctx}>{children}</SearchCtx.Provider>
          {empty && <div className="px-2 py-3 text-center text-xs text-muted-foreground" data-testid="select-search-empty">لا توجد نتائج</div>}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props} />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef(({ className, children, disabled, onPointerMove, alwaysShow, ...props }, ref) => {
  const ctx = React.useContext(SearchCtx)
  const reg = ctx?.register
  React.useEffect(() => reg?.(), [reg])
  const hidden = !alwaysShow && !!ctx?.q && !normSearch(`${nodeText(children)} ${props.textValue || ""}`).includes(ctx.q)
  return (
  <SelectPrimitive.Item
    ref={ref}
    disabled={disabled || hidden}
    data-search-hidden={hidden ? "" : undefined}
    onPointerMove={ctx?.active ? (e) => { onPointerMove?.(e); e.preventDefault() } : onPointerMove}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      ctx?.active && "hover:bg-accent hover:text-accent-foreground",
      hidden && "hidden",
      className
    )}
    {...props}>
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props} />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
